import {
  CaptureGroup,
  CaptureStatus,
  MultiPointInstallation,
  MultiPointInstallationAccuracy,
} from '../../types/api';
import {
  MultiPointInstallationCaptureGroupAction,
  MultiPointInstallationCaptureGroupActionTypes,
  MultiPointInstallationCaptureGroupLoadAction,
  MultiPointInstallationCaptureGroupState,
} from '../../types/multi-point-installation-capture-group';
import {
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';
import { DispatchType } from '../../types/rx-actions';
import { actions } from '../index';
import { getRequiredSessionToken } from '../../utils/session-token';

import createRxStore, { ofType, rxDispatch } from '..';
import { SenseiApi } from '../../utils/api';

export const initialState: MultiPointInstallationCaptureGroupState = {
  resource: RESOURCE_IDLE,
};

function reducer(
  state: MultiPointInstallationCaptureGroupState,
  action: MultiPointInstallationCaptureGroupAction,
): MultiPointInstallationCaptureGroupState {
  switch (action.type) {
    case MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_LOADING:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };
    case MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            captureGroup: action.captureGroup,
            mpi: action.mpi,
            mpiAccuracy: action.mpiAccuracy,
          },
        },
      };
    case MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_ERROR:
      return {
        ...state,
        resource: {
          status: ResourceStatus.ERROR,
          error: action.error,
        },
      };
    default:
      return state;
  }
}

const MultiPointInstallationCaptureGroupStore = createRxStore(
  'MultiPointInstallationCaptureGroupStore',
  initialState,
  reducer,
);

async function load(
  dispatch: DispatchType,
  jwtToken: string,
  mpiId: MultiPointInstallation['id'],
  captureGroupId: CaptureGroup['id'],
  reloading = false,
) {
  // Load MPI details and capture group
  if (!reloading) {
    dispatch({
      type: MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_LOADING,
    });
  }
  let captureGroup: CaptureGroup;
  let mpi: MultiPointInstallation;
  let mpiAccuracy: MultiPointInstallationAccuracy;
  try {
    [mpi, captureGroup, mpiAccuracy] = await Promise.all([
      SenseiApi.multiPointInstallationDetail(jwtToken, mpiId),
      SenseiApi.multiPointInstallationCaptureGroup(
        jwtToken,
        mpiId,
        captureGroupId,
      ),
      SenseiApi.multiPointInstallationAccuracy(jwtToken, mpiId),
    ]);
  } catch (error) {
    dispatch({
      type: MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_ERROR,
      error: error,
    });
    return;
  }

  dispatch({
    type: MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_LOAD_COMPLETE,
    captureGroup,
    mpi,
    mpiAccuracy,
  });
}

const initialLoad = (
  dispatch: DispatchType,
  jwtToken: string,
  mpiId: MultiPointInstallation['id'],
  captureGroupId: CaptureGroup['id'],
) => {
  return load(dispatch, jwtToken, mpiId, captureGroupId, false);
};

const reload = (
  dispatch: DispatchType,
  jwtToken: string,
  mpiId: MultiPointInstallation['id'],
  captureGroupId: CaptureGroup['id'],
) => {
  return load(dispatch, jwtToken, mpiId, captureGroupId, true);
};

// =================================
// SIDE EFFECT: fetch capture group details on page load and when reloading via polling is requested
// =================================

actions
  .pipe(
    ofType(
      MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_REQUEST_LOAD,
    ),
  )
  .subscribe((action: MultiPointInstallationCaptureGroupLoadAction) => {
    const jwtToken = getRequiredSessionToken();

    initialLoad(rxDispatch, jwtToken, action.mpiId, action.captureGroupId);
  });

actions
  .pipe(
    ofType(
      MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_REQUEST_RELOAD,
    ),
  )
  .subscribe((action: MultiPointInstallationCaptureGroupLoadAction) => {
    const jwtToken = getRequiredSessionToken();

    reload(rxDispatch, jwtToken, action.mpiId, action.captureGroupId);
  });

export default MultiPointInstallationCaptureGroupStore;
